import '../chatbot/chatbot.css';

import React, { useEffect, useRef, useState } from 'react';

import type { FlowEngine } from '../../services/flows';
import type { Chip, Step } from '../../services/flows/types';
import { VariableResolver } from '../../services/flows/variableResolver';
import { logger } from '../../utils/logger';

// Create component logger for ChipsStep
const chipsStepLogger = logger.withComponent('ChipsStep');

interface ChipsStepProps {
  step: Step;
  engine: FlowEngine;
  onChipSelect: (chipId: string) => void;
  onMessage: (message: any) => void;
}

const ChipsStep: React.FC<ChipsStepProps> = ({
  step,
  engine,
  onChipSelect,
  onMessage,
}) => {
  const [selectedChipId, setSelectedChipId] = useState<string | null>(null);
  const [visible, setVisible] = useState<boolean>(false);
  const chipsContainerRef = useRef<HTMLDivElement>(null);

  // When this step becomes active, emit its content as a message to the parent.
  useEffect(() => {
    if (step.content) {
      const flowState = engine.getState();
      const variables = flowState.variables;
      // Translate content first, then resolve any variables
      const translatedContent = __(step.content);
      const resolvedContent = VariableResolver.interpolate(translatedContent, variables);

      const message = {
        from: 'bot',
        text: resolvedContent,
      };

      onMessage(message);
    }

    // Set up 300ms delay for chip appearance as per UX specification (Section 3.6)
    const visibilityTimer = setTimeout(() => {
      setVisible(true);
    }, 300);

    // Cleanup function to clear timeout if component unmounts
    return () => clearTimeout(visibilityTimer);
  }, [step.content, engine, onMessage]);

  // Separate useEffect to handle scrolling after chips become visible
  useEffect(() => {
    if (!visible) return;

    // Ensure chips are visible after they appear with delay
    const scrollTimer = setTimeout(() => {
      if (!chipsContainerRef.current) {
        chipsStepLogger.info('[ChipsStep] No chips container found');
        return;
      }

      const chatPanel = chipsContainerRef.current.closest('.chat-panel') as HTMLElement;
      if (!chatPanel) {
        chipsStepLogger.info('[ChipsStep] No chat panel found');
        return;
      }

      // Check if chips are visible in viewport
      const containerRect = chatPanel.getBoundingClientRect();
      const chipsRect = chipsContainerRef.current.getBoundingClientRect();

      chipsStepLogger.info('[ChipsStep] Scroll check:', {
        containerBottom: containerRect.bottom,
        chipsBottom: chipsRect.bottom,
        chipsTop: chipsRect.top,
        needsScroll: chipsRect.bottom > containerRect.bottom,
        scrollTop: chatPanel.scrollTop,
        scrollHeight: chatPanel.scrollHeight,
        clientHeight: chatPanel.clientHeight,
      });

      // If chips are below the visible area, scroll them into view
      if (chipsRect.bottom > containerRect.bottom) {
        chipsStepLogger.info('[ChipsStep] Fallback scroll triggered - scrolling to bottom');
        chatPanel.scrollTop = chatPanel.scrollHeight;
      } else {
        chipsStepLogger.info('[ChipsStep] Fallback scroll not needed - chips are visible');
      }
    }, 0);

    return () => clearTimeout(scrollTimer);
  }, [visible]);

  const handleChipClick = (chip: Chip) => {
    if (selectedChipId) return; // Already selected

    setSelectedChipId(chip.id);
    onChipSelect(chip.id);
  };

  // This component only renders the interactive chips.
  // The message content is handled by the onMessage callback.
  if (!step.chips || selectedChipId || !visible) {
    return null;
  }

  return (
    <div className="chips-container" ref={chipsContainerRef}>
      {step.chips.map((chip) => (
        <button
          key={chip.id}
          className="chip"
          onClick={() => handleChipClick(chip)}
          type="button"
          aria-label={`${__('Select option')}: ${__(chip.label)}`}
        >
          {__(chip.label)}
        </button>
      ))}
    </div>
  );
};

export default ChipsStep;